import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import ChartActionsMenu from '@/components/molecules/ChartActionsMenu';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import type { HealthWoundTrendPoint } from '@/domain/health';
import chartBlockStyles from '@/routes/Client/Cage/pages/Health/components/HealthDetailChartContentBlock.module.scss';
import { parseLocale } from '@/utils/localeFormatters';

import { buildWoundFishCountChartSpec } from './healthWoundsFishCountChart';
import { WOUND_GRAPH_COLORS, type WoundGraphSeriesConfig } from './healthWoundsGraphHelpers';
import { useHealthWoundGraphChart } from './useHealthWoundGraphChart';

import styles from './HealthWoundsBodyGraphsView.module.scss';

const WOUND_GRAPH_CHART_HEIGHT = 360;

export interface HealthWoundsBodyGraphsViewProps {
    trendPoints: HealthWoundTrendPoint[];
    isLoading: boolean;
}

interface WoundGraphCardProps {
    title: string;
    chartName: string;
    trendPoints: HealthWoundTrendPoint[];
    woundSeries: WoundGraphSeriesConfig[];
    isLoading: boolean;
}

function WoundGraphCard(props: WoundGraphCardProps) {
    const { title, chartName, trendPoints, woundSeries, isLoading } = props;
    const { t } = useTranslation('healthModule');

    const chart = useHealthWoundGraphChart({ trendPoints, woundSeries });

    const hasNoData = !isLoading && trendPoints.length === 0;

    return (
        <ContentBlock
            title={title}
            size="compact"
            spacing="none"
            fill
            className={chartBlockStyles.chartBlock}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartActionsMenu
                    chartRef={chart.chartRef}
                    chartName={chartName}
                    isFullScreen={chart.isFullScreen}
                    disabled={isLoading || hasNoData}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                />
            }>
            <div className={`${chartBlockStyles.chartBlockBody} ${styles.chartBody}`}>
                {isLoading ? (
                    <SkeletonBox width="100%" height={WOUND_GRAPH_CHART_HEIGHT} borderRadius={8} />
                ) : hasNoData ? (
                    <EmptyState
                        title={t('wounds.graphsNoData')}
                        appearance="quiet"
                        live="polite"
                        height={WOUND_GRAPH_CHART_HEIGHT}
                    />
                ) : (
                    <MetricChart
                        kind="line"
                        chartRef={chart.chartRef}
                        data={chart.data}
                        options={chart.options}
                        height={WOUND_GRAPH_CHART_HEIGHT}
                        legendItems={chart.legendItems}
                        visibleSeries={chart.visibleSeries}
                        onToggleSeries={chart.toggleSeries}
                    />
                )}
            </div>
        </ContentBlock>
    );
}

interface FishCountGraphCardProps {
    trendPoints: HealthWoundTrendPoint[];
    isLoading: boolean;
}

function FishCountGraphCard(props: FishCountGraphCardProps) {
    const { trendPoints, isLoading } = props;
    const { t, i18n } = useTranslation('healthModule');
    const { t: tBehavior } = useTranslation('behaviorModule');
    const locale = parseLocale(i18n.language);

    const spec = useMemo(
        () => buildWoundFishCountChartSpec(trendPoints, locale, tBehavior('common.measurements')),
        [trendPoints, locale, tBehavior]
    );
    const chart = useMetricChart(spec);

    const hasNoData = !isLoading && trendPoints.length === 0;

    return (
        <ContentBlock
            title={t('dashboard.export.fishCount')}
            subtitle={t('wounds.graphsFishCountSubtitle')}
            size="compact"
            spacing="none"
            fill
            className={chartBlockStyles.chartBlock}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartActionsMenu
                    chartRef={chart.chartRef}
                    chartName="health-wounds-fish-count-chart"
                    isFullScreen={chart.isFullScreen}
                    disabled={isLoading || hasNoData}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                />
            }>
            <div className={`${chartBlockStyles.chartBlockBody} ${styles.chartBody}`}>
                {isLoading ? (
                    <SkeletonBox width="100%" height={WOUND_GRAPH_CHART_HEIGHT} borderRadius={8} />
                ) : hasNoData ? (
                    <EmptyState
                        title={t('wounds.graphsNoData')}
                        appearance="quiet"
                        live="polite"
                        height={WOUND_GRAPH_CHART_HEIGHT}
                    />
                ) : (
                    <MetricChart
                        kind="bar"
                        data={chart.data}
                        options={chart.options}
                        legendItems={chart.legendItems}
                        visibleSeries={chart.visibleSeries}
                        onToggleSeries={chart.toggleSeries}
                        chartRef={chart.chartRef}
                        height={WOUND_GRAPH_CHART_HEIGHT}
                    />
                )}
            </div>
        </ContentBlock>
    );
}

export default function HealthWoundsBodyGraphsView(props: HealthWoundsBodyGraphsViewProps) {
    const { trendPoints, isLoading } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    const typesSeries: WoundGraphSeriesConfig[] = useMemo(
        () => [
            {
                label: tGlobal('fishHealthIndicators.activeWounds'),
                color: WOUND_GRAPH_COLORS.active,
                getValue: (point) => point.woundTypes.numWounds,
            },
            {
                label: tGlobal('fishHealthIndicators.healingWounds'),
                color: WOUND_GRAPH_COLORS.healing,
                getValue: (point) => point.woundTypes.numHealingWounds,
            },
        ],
        [tGlobal]
    );

    const sizeSeries: WoundGraphSeriesConfig[] = useMemo(
        () => [
            {
                label: tGlobal('fishHealthIndicators.bigWound'),
                color: WOUND_GRAPH_COLORS.large,
                getValue: (point) => point.sizeDistribution.bigWound,
            },
            {
                label: tGlobal('fishHealthIndicators.mediumWound'),
                color: WOUND_GRAPH_COLORS.medium,
                getValue: (point) => point.sizeDistribution.mediumWound,
            },
            {
                label: tGlobal('fishHealthIndicators.smallWound'),
                color: WOUND_GRAPH_COLORS.small,
                getValue: (point) => point.sizeDistribution.smallWound,
            },
        ],
        [tGlobal]
    );

    const distributionSeries: WoundGraphSeriesConfig[] = useMemo(
        () => [
            {
                label: tGlobal('fishHealthIndicators.oneWound'),
                color: WOUND_GRAPH_COLORS.single,
                getValue: (point) => point.distributionOfWounds.oneWound,
            },
            {
                label: tGlobal('fishHealthIndicators.moreThanOneWound'),
                color: WOUND_GRAPH_COLORS.multiple,
                getValue: (point) => point.distributionOfWounds.moreThanOneWound,
            },
        ],
        [tGlobal]
    );

    return (
        <div className={styles.chartsGrid}>
            <WoundGraphCard
                title={t('wounds.graphsTypesTitle')}
                chartName="health-wounds-types-chart"
                trendPoints={trendPoints}
                woundSeries={typesSeries}
                isLoading={isLoading}
            />
            <WoundGraphCard
                title={t('wounds.graphsSizeTitle')}
                chartName="health-wounds-size-chart"
                trendPoints={trendPoints}
                woundSeries={sizeSeries}
                isLoading={isLoading}
            />
            <WoundGraphCard
                title={t('wounds.graphsDistributionTitle')}
                chartName="health-wounds-distribution-chart"
                trendPoints={trendPoints}
                woundSeries={distributionSeries}
                isLoading={isLoading}
            />
            <FishCountGraphCard trendPoints={trendPoints} isLoading={isLoading} />
        </div>
    );
}
